import dynamic from 'next/dynamic';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@natu/ui/Accordion';
import { DynamicIcon } from '@natu/ui/DynamicIcon';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@natu/ui/Tabs';
import { cn } from '@natu/utils/cn';

import { type StoryblokRichTextData } from '../../../../richtext';
import {
  type BlokItem,
  DynamicRender,
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
} from '../../../../utils';

const StoryblokRichtext = dynamic(() =>
  import('../../../../richtext').then(mod => mod.StoryblokRichtext),
);

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface ItemProp extends BlokItem<'benefitItem'> {
  title?: string;
  cardImage?: StoryblokAsset;
  cardDescription?: StoryblokRichTextData;
}

interface SBBenefitsSectionProps {
  title?: string;
  subtitle?: string;
  items?: ItemProp[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

const getSupportItemData = ({ items }: { items?: ItemProp[] }) =>
  items?.filter(item => item.component === 'benefitItem');

export const SBBenefitsSection = ({ blok }: SBProps<SBBenefitsSectionProps>) => {
  const { title, subtitle, mbMobile, mbTablet, mbDesktop, mtMobile, mtTablet, mtDesktop, items } =
    blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const data = getSupportItemData({ items });

  return (
    <section className={cn(className, 'flex flex-col gap-y-10 lg:gap-y-24')} {...sbEditable(blok)}>
      <header className="grid gap-8 lg:grid-cols-[1fr_auto]">
        <div className="grid max-w-4xl gap-3 lg:gap-5">
          {subtitle && (
            <p className="font-permanent-marker text-primary-600 text-xl lg:text-[28px]">
              {subtitle}
            </p>
          )}
          {subtitle && (
            <h2 className="text-naturaily-vulcan text-[28px] font-semibold lg:text-[46px]">
              {title}
            </h2>
          )}
        </div>
        <div className="flex lg:flex-col lg:items-end">
          <div className="bg-naturaily-butterscotch h-[24px] w-[24px] rounded-full lg:mb-5 lg:h-[30px] lg:w-[30px]" />
          <div className="bg-naturaily-airborne ml-2 h-[40px] w-[40px] rounded-bl-full lg:h-[50px] lg:w-[58px]" />
        </div>
      </header>

      <div className="lg:hidden">
        <Accordion type="single" className="flex flex-col gap-y-4">
          {data?.map(item => {
            const imageData = getAssetFromStoryblok(item.cardImage);

            return (
              <AccordionItem key={item._uid} value={item._uid} className="group">
                <AccordionTrigger className="data-[state=open]:bg-primary-600 mb-4 items-center rounded-lg bg-gray-100 px-4 py-3.5 pr-6 text-left text-lg font-semibold text-gray-600 data-[state=open]:text-white group-last:data-[state=closed]:mb-0">
                  {item.title}
                  <DynamicIcon icon="chevron" />
                </AccordionTrigger>
                <AccordionContent>
                  <div className="mb-4 grid gap-4 overflow-hidden rounded-b-3xl">
                    {item.cardDescription && (
                      <StoryblokRichtext
                        className="text-md leading-6 text-gray-600 md:text-sm md:leading-[22px]"
                        data={item.cardDescription}
                      />
                    )}
                    <div className="relative">
                      <div className="bg-naturaily-butterscotch absolute -top-[11px] right-6 h-[22px] w-[22px] rounded-full" />
                      {imageData?.src && (
                        <ResponsiveImage
                          className="max-h-[270px] w-full object-cover"
                          {...imageData}
                        />
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <Tabs
        defaultValue={data?.[0]._uid}
        className="hidden w-full gap-x-16 pb-10 lg:flex xl:gap-x-[136px]"
      >
        <TabsList className="flex w-full max-w-[456px] flex-col gap-y-3">
          {data?.map(item => (
            <TabsTrigger
              key={item._uid}
              value={item._uid}
              className="text-naturaily-cascade data-[state=active]:text-naturaily-vulcan text-display-md-34 items-center justify-start gap-x-6 rounded-lg border-0 px-4 py-3.5 font-bold opacity-40 data-[state=active]:border-0 data-[state=active]:bg-inherit data-[state=active]:opacity-100"
            >
              {item.title}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="w-full">
          {data?.map(item => (
            <TabsContent className="h-full max-w-[616px]" key={item._uid} value={item._uid}>
              <DynamicRender data={item} />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </section>
  );
};
